import { formatCurrency } from './currency';

export function getCheckPrepaymentHint(balance, t) {
  if (!balance?.hasPrepayment) return null;
  return t('checks.prepaymentOnAccount', { amount: formatCurrency(balance.totalDeposits, balance.checkCurrency) });
}

export function buildBalanceCardEntries({
  totalItems,
  totalDeposits,
  amountDue,
  subtotal,
  vatAmount,
  vatPercentage,
  pricesIncludeVat,
  checkCurrency,
  defaultItemCurrency,
  showDualCurrency,
  subtotalInDefaultCurrency,
  vatAmountInDefaultCurrency,
  totalInDefaultCurrency,
  totalDepositsInDefaultCurrency,
  amountDueInDefaultCurrency,
  compact,
}, t) {
  const showVat = vatPercentage > 0 && !compact;
  const dual = showDualCurrency && defaultItemCurrency && checkCurrency
    && defaultItemCurrency !== checkCurrency;

  const fmt = (amountDefault, amountCheck) => {
    if (!dual) {
      return { value: formatCurrency(amountCheck ?? amountDefault, checkCurrency || defaultItemCurrency) };
    }
    return {
      value: formatCurrency(amountCheck, checkCurrency),
      valueSecondary: formatCurrency(amountDefault, defaultItemCurrency),
    };
  };

  const labels = compact
    ? {
      items: t('common.total'),
      deposits: t('checks.compactPaid'),
      due: t('checks.compactDue'),
    }
    : {
      items: t('cashiering.totalItems'),
      deposits: t('cashiering.payments'),
      due: t('cashiering.amountDue'),
      subtotal: t('checks.subtotal'),
      vat: pricesIncludeVat
        ? t('checks.vatIncluded', { percent: vatPercentage })
        : t('checks.vatAdded', { percent: vatPercentage }),
    };

  return [
    showVat && {
      key: 'subtotal',
      label: labels.subtotal,
      color: 'text.primary',
      ...fmt(subtotalInDefaultCurrency ?? subtotal, subtotal),
    },
    showVat && {
      key: 'vat',
      label: labels.vat,
      color: 'text.secondary',
      ...fmt(vatAmountInDefaultCurrency ?? vatAmount, vatAmount),
    },
    {
      key: 'total',
      label: labels.items,
      color: 'primary.main',
      ...fmt(totalInDefaultCurrency ?? totalItems, totalItems),
    },
    {
      key: 'deposits',
      label: labels.deposits,
      color: 'info.main',
      ...fmt(totalDepositsInDefaultCurrency ?? totalDeposits, totalDeposits),
    },
    {
      key: 'due',
      label: labels.due,
      color: (amountDue || 0) > 0 ? 'error.main' : 'success.main',
      bold: true,
      ...fmt(amountDueInDefaultCurrency ?? amountDue, amountDue),
    },
  ].filter(Boolean);
}

export function buildClientBalanceCardEntries({
  totalItems,
  totalPaid,
  totalDeposits,
  amountDue,
  checkCurrency,
  defaultItemCurrency,
  showDualCurrency,
  totalInDefaultCurrency,
  totalPaidInDefaultCurrency,
  totalDepositsInDefaultCurrency,
  amountDueInDefaultCurrency,
}, t) {
  const dual = showDualCurrency && defaultItemCurrency && checkCurrency
    && defaultItemCurrency !== checkCurrency;

  const fmt = (amountDefault, amountCheck) => {
    if (!dual) {
      return { value: formatCurrency(amountCheck ?? amountDefault, checkCurrency || defaultItemCurrency) };
    }
    return {
      value: formatCurrency(amountCheck, checkCurrency),
      valueSecondary: formatCurrency(amountDefault, defaultItemCurrency),
    };
  };

  return [
    {
      key: 'totalItems',
      label: t('cashiering.totalItems'),
      color: 'primary.main',
      ...fmt(totalInDefaultCurrency ?? totalItems, totalItems),
    },
    {
      key: 'paid',
      label: t('checks.compactPaid'),
      color: 'success.main',
      ...fmt(totalPaidInDefaultCurrency ?? totalPaid, totalPaid),
    },
    {
      key: 'deposits',
      label: t('clients.balanceDeposits'),
      color: 'info.main',
      ...fmt(totalDepositsInDefaultCurrency ?? totalDeposits, totalDeposits),
    },
    {
      key: 'due',
      label: t('checks.compactDue'),
      color: (amountDue || 0) > 0 ? 'error.main' : 'success.main',
      bold: true,
      ...fmt(amountDueInDefaultCurrency ?? amountDue, amountDue),
    },
  ];
}

export function balanceCardGridXs(cardCount) {
  return cardCount <= 3 ? 4 : (12 / Math.min(cardCount, 4));
}
